import { LoadingButton } from '@mui/lab';
import { Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { QueryKeys } from 'entities/user/api/common';
import { useUpdateProfile } from 'entities/user/api/useUpdateProfile';
import { useAuth } from 'shared/lib/auth/useAuth';
import { Form } from 'shared/lib/forms/Form';
import { isRequired } from 'shared/lib/validation/isRequired';
import { AuthHeroLayout } from 'shared/ui/AuthHeroLayout';
import { TextFormField } from 'shared/ui/TextFormField';

type CompleteProfileValues = {
  first_name: string;
  last_name: string;
};

/** Asks for the name that the Auth0 "Set up your profile" form used to collect after signup. */
export const CompleteProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { updateProfile, isUpdating, error } = useUpdateProfile();

  const formMethods = useForm<CompleteProfileValues>({
    mode: 'onChange',
    defaultValues: {
      first_name: user?.profile?.first_name ?? '',
      last_name: user?.profile?.last_name ?? '',
    },
  });

  // AuthGuard reads the name from the live profile, so writing the response to the cache moves the user on.
  const onSubmit = (data: CompleteProfileValues) => {
    updateProfile(data, {
      onSuccess: (profile) => queryClient.setQueryData([QueryKeys.GetProfile], profile),
    });
  };

  return (
    <AuthHeroLayout>
      <Form className="flex flex-col gap-6" error={error} formMethods={formMethods} onSubmit={onSubmit}>
        <Typography variant="h1" fontSize={{ xs: 32, sm: 44 }} fontWeight={500} lineHeight={1.1} className="mb-4">
          Set up your profile
        </Typography>
        <TextFormField
          autoFocus
          autoComplete="given-name"
          label="First name"
          name="first_name"
          rules={{ validate: { required: isRequired('First name is required') } }}
        />
        <TextFormField
          autoComplete="family-name"
          label="Last name"
          name="last_name"
          rules={{ validate: { required: isRequired('Last name is required') } }}
        />
        <LoadingButton fullWidth loading={isUpdating} size="large" type="submit" variant="contained">
          Continue
        </LoadingButton>
      </Form>
    </AuthHeroLayout>
  );
};
